import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Text } from '../../../../components';
import { Sheet, useSheetDismiss } from '../../../../components/Sheet';
import { GenerateButton } from '../../../creatives/components/GenerateButton';
import { LumioMark } from '../../../creatives/components/LumioMark';
import { Body, Intro, Lead, Facts, Item } from './LookActionSheet.styles';

/** What the sheet asks for: drawing the website's art in a look (`draw`), or switching the website to it (`apply`). */
export type LookAction = 'draw' | 'apply';

export type LookActionSheetProps = {
  action: LookAction;
  /** The look's name, as owners read it. */
  lookName: string;
  /** Switching pauses the brand's custom style in use (new banners stop following it): the sheet says so. */
  pausesStyle: boolean;
  /** A site job runs or a request is on its way (the confirm waits for it). */
  busy: boolean;
  /** Credits drawing the look's art costs, charged once when it starts: listed with the facts and shown on the confirm
   *  (undefined until the price table loads: neither shows a price rather than guess). Switching (`apply`) is free and
   *  never shows it. */
  cost?: number;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Before a website look changes: "Generate art in this style" (Lumio draws all of the website's art in the look next
 * to the current one, without changing what players see; it takes a while, and costs credits charged once when it
 * starts) or "Set as primary" (the website switches to a look whose art is drawn, in a few minutes; free, since
 * nothing is redrawn). Both run to the end once started.
 */
export const LookActionSheet = ({
  action,
  lookName,
  pausesStyle,
  busy,
  cost,
  onConfirm,
  onClose,
}: LookActionSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const look = { look: lookName };
  const draw = action === 'draw';
  const confirm = (): void => {
    if (busy) return;
    onConfirm();
    dismiss();
  };

  return (
    <Sheet
      title={draw ? t('Generate art in this style') : t('Set as primary')}
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="520px"
      footer={
        <>
          <Button label={t('Cancel')} variant="outline" onClick={dismiss} />
          {draw ? (
            <GenerateButton compact label={t('Generate art')} cost={cost} disabled={busy} onClick={confirm} />
          ) : (
            <Button label={t('Switch my site')} disabled={busy} onClick={confirm} />
          )}
        </>
      }
    >
      <Body>
        <Intro>
          {draw ? <LumioMark size={28} /> : <Icon name="globe" size={24} />}
          {draw ? (
            <Lead
              text="Lumio draws all of your website art in {{look}}, next to your current look."
              placeholders={look}
              tag="span"
            />
          ) : (
            <Lead text="Your website switches to {{look}}." placeholders={look} tag="span" />
          )}
        </Intro>
        <Facts>
          {draw ? (
            <>
              <Item>
                <Icon name="eye" size={14} />
                <Text text="Players keep seeing your current look while it is drawn." tag="span" />
              </Item>
              <Item>
                <Icon name="clock" size={14} />
                <Text
                  text="It can take tens of minutes and runs to the end once started. You can leave this page."
                  tag="span"
                />
              </Item>
              <Item>
                <Icon name="globe" size={14} />
                <Text text="Once it is drawn, set it as primary to show it to players." tag="span" />
              </Item>
              {cost != null ? (
                <Item>
                  <Icon name="wallet" size={14} />
                  <Text text={cost === 1 ? '{{n}} credit' : '{{n}} credits'} placeholders={{ n: cost }} tag="span" />
                </Item>
              ) : null}
            </>
          ) : (
            <>
              <Item>
                <Icon name="clock" size={14} />
                <Text text="Nothing is redrawn. Players see the change in a few minutes." tag="span" />
              </Item>
              {pausesStyle ? (
                <Item>
                  <Icon name="palette" size={14} />
                  <Text
                    text="Your custom art style is paused, so new banners and images stop following it."
                    tag="span"
                  />
                </Item>
              ) : null}
              <Item>
                <Icon name="info" size={14} />
                <Text text="Switching your site is free." tag="span" />
              </Item>
            </>
          )}
        </Facts>
      </Body>
    </Sheet>
  );
};

export default LookActionSheet;
